import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { SurveysService, SurveyDetailsResponse, QuestionDetailsResponse, QuestionType, SurveySubmissionRequest, SurveyAnswerSubmissionRequest } from '@src/app/shared/backend/clients';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { SessionService } from '@app/shared/services/session.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-take-survey',
    templateUrl: './take-survey.component.html',
    styleUrl: './take-survey.component.scss',
    host: {
        class: 'app-take-survey'
    },
    standalone: false
})
export class TakeSurveyComponent implements OnInit, OnDestroy {

  private destroyedComponent$ = new Subject<void>();

  selectedLanguage = 'en';

  surveyId!: number;
  survey: SurveyDetailsResponse | null = null;
  visibleQuestions: QuestionDetailsResponse[] = [];

  surveyQuestionsForm: FormGroup = this.fb.group({
    questions: this.fb.array([])
  });

  loading = false;
  submittingForm = false;

  readonly questionType = QuestionType;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private toasterService: ToasterService,
    private surveysService: SurveysService,
    private session: SessionService
  ) { }

  ngOnInit() {
    this.selectedLanguage = this.getCurrentLanguage();
    this.listenForLanguageChanges();
    this.init();
  }

  init(): void {
    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(params => {
        this.surveyId = Number(params['surveyId']);

        if (this.surveyId) {
          this.loadSurvey();
        }
      });
  }

  get isSurveyAvailable(): boolean {
    return !!this.survey?.isPublished && !!this.survey?.isActive;
  }

  get questionsFormArray(): FormArray {
    return this.surveyQuestionsForm.get('questions') as FormArray;
  }

  getAnswerControlName(questionId: number): string {
    return `answer_${questionId}`;
  }

  loadSurvey(): void {
    this.loading = true;

    this.surveysService.apiSurveysDetailsIdGet(this.surveyId)
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe({
        next: (response) => {
          this.survey = response;
          this.applySelectedLanguage();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  listenForLanguageChanges() {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(event => {
        this.selectedLanguage = event.lang;
        this.applySelectedLanguage();
      });
  }

  getCurrentLanguage(): string {
    if (this.translate.currentLang) {
      return this.translate.currentLang;
    }

    if (this.session.locale) {
      return this.session.locale;
    }

    const storedLanguage = localStorage.getItem('locale');
    if (storedLanguage) {
      return storedLanguage;
    }

    return 'en';
  }

  applySelectedLanguage(): void {
    if (!this.survey) {
      return;
    }

    this.selectedLanguage = this.getCurrentLanguage();
    this.visibleQuestions = this.survey.questions ?? [];

    this.buildQuestions();
  }

  hasQuestionTranslation(question: QuestionDetailsResponse, languageCode: string): boolean {
    if (!question.questionTranslations) {
      return false;
    }

    const normalizedSelectedLanguage = this.normalizeLanguageCode(languageCode);

    for (const translation of question.questionTranslations) {
      const normalizedTranslationLanguage = this.normalizeLanguageCode(translation.isoCode);

      if (normalizedTranslationLanguage === normalizedSelectedLanguage) {
        return true;
      }
    }

    return false;
  }

  normalizeLanguageCode(languageCode?: string | null): string {
    if (!languageCode) {
      return '';
    }

    return languageCode.toLowerCase();
  }

  buildQuestions(): void {
    this.questionsFormArray.clear();

    this.visibleQuestions.forEach((question) => {
      const answerControlName = this.getAnswerControlName(question.id);

      if (question.questionType === QuestionType.FixedValues) {
        if (question.allowsMultipleAnswers) {
          this.questionsFormArray.push(
            this.fb.group({
              questionId: [question.id],
              type: [question.questionType],
              answers: this.fb.array(
                question.answerOptions?.map(() => new FormControl(false)) ?? []
              )
            })
          );
        } else {
          this.questionsFormArray.push(
            this.fb.group({
              questionId: [question.id],
              type: [question.questionType],
              [answerControlName]: [null]
            })
          );
        }
      }

      if (question.questionType === QuestionType.Text) {
        this.questionsFormArray.push(
          this.fb.group({
            questionId: [question.id],
            type: [question.questionType],
            [answerControlName]: ['']
          })
        );
      }

      if (question.questionType === QuestionType.Number) {
        this.questionsFormArray.push(
          this.fb.group({
            questionId: [question.id],
            type: [question.questionType],
            [answerControlName]: [null]
          })
        );
      }
    });
  }

  getCheckboxArray(questionIndex: number): FormArray {
    return this.questionsFormArray.at(questionIndex).get('answers') as FormArray;
  }

  getQuestionTranslation(question: QuestionDetailsResponse): string {
    const translation = question.questionTranslations?.find(item =>
      this.normalizeLanguageCode(item.isoCode) === this.normalizeLanguageCode(this.selectedLanguage)
    );

    return translation?.translation ?? question.code ?? '';
  }

  getAnswerTranslation(answer: any): string {
    const translation = answer.answerTranslations?.find((item: any) =>
      this.normalizeLanguageCode(item.isoCode) === this.normalizeLanguageCode(this.selectedLanguage)
    );

    return translation?.translatedAnswer ?? answer.code ?? '';
  }

  submitForm(): void {
    try {
      const payload = this.mapFormToSubmissionRequest();

      if (!payload) {
        return;
      }

      this.submittingForm = true;

      this.surveysService.apiSurveysSubmissionPost(payload)
        .pipe(takeUntil(this.destroyedComponent$))
        .subscribe({
          next: () => {
            this.submittingForm = false;

            this.toasterService.show(
              'success',
              this.translate.instant('TAKE_SURVEY.TOAST_SUCCESS_TITLE_SURVEY_SUBMITTED_SUCCESS'),
              this.translate.instant('TAKE_SURVEY.TOAST_SUCCESS_MESSAGE_SURVEY_SUBMITTED_SUCCESS')
            );

            this.clearSurveyForm();
            this.router.navigate(['/surveys']);
          },
          error: () => {
            this.submittingForm = false;

            this.toasterService.show(
              'error',
              this.translate.instant('TAKE_SURVEY.TOAST_ERROR_TITLE_SURVEY_SUBMITTED_ERROR'),
              this.translate.instant('TAKE_SURVEY.TOAST_ERROR_MESSAGE_SURVEY_SUBMITTED_ERROR')
            );
          }
        });

    } catch {
      this.submittingForm = false;
    }
  }

  mapFormToSubmissionRequest(): SurveySubmissionRequest | null {
    if (!this.survey) {
      return null;
    }

    const surveyAnswerSubmissions: SurveyAnswerSubmissionRequest[] =
      this.visibleQuestions.map((question, index) => {
        const questionForm = this.questionsFormArray.at(index);

        if (question.questionType === QuestionType.FixedValues) {
          return this.mapFixedValueQuestion(question, questionForm);
        }

        if (question.questionType === QuestionType.Text) {
          return {
            questionId: question.id,
            textValue: questionForm.get(this.getAnswerControlName(question.id))?.value ?? null
          };
        }

        if (question.questionType === QuestionType.Number) {
          return {
            questionId: question.id,
            numericValue: questionForm.get(this.getAnswerControlName(question.id))?.value ?? null
          };
        }

        return {
          questionId: question.id
        };
      });

    return {
      surveyId: this.survey.id,
      surveyAnswerSubmissions
    };
  }

  mapFixedValueQuestion(
    question: QuestionDetailsResponse,
    questionForm: AbstractControl
  ): SurveyAnswerSubmissionRequest {

    if (question.allowsMultipleAnswers) {
      const checkboxValues: boolean[] = questionForm.get('answers')?.value ?? [];

      const selectedOptions =
        question.answerOptions
          ?.filter((_, optionIndex) => checkboxValues[optionIndex])
          .map(option => ({
            answerOptionId: option.id
          })) ?? [];

      return {
        questionId: question.id,
        surveyAnswerOptionSubmissions: selectedOptions
      };
    }

    const selectedAnswerId = questionForm.get(this.getAnswerControlName(question.id))?.value;

    return {
      questionId: question.id,
      surveyAnswerOptionSubmissions: selectedAnswerId ? [{ answerOptionId: selectedAnswerId }] : []
    };
  }

  clearSurveyForm(): void {
    this.surveyQuestionsForm.reset();
    this.questionsFormArray.clear();
    this.visibleQuestions = [];
    this.survey = null;
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
